import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { comics, categories } from '@/mocks/comics';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-4">该漫画可能已被移除或链接失效</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-home-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const catName = categories.find((c) => c.slug === comic.category)?.name || comic.category;
  const relatedComics = comics
    .filter((c) => c.id !== comic.id && c.category === comic.category)
    .slice(0, 4);

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="max-w-[1280px] mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: `${catName}漫画`, href: `/category/${comic.category}` },
              { label: comic.title },
            ]}
          />

          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": `${comic.title} - 韩国漫画在线阅读`,
            "description": comic.description,
            "author": {
              "@type": "Person",
              "name": comic.author
            },
            "dateModified": comic.lastUpdated,
            "inLanguage": "zh-CN",
            "url": `https://www.hanmanwang.com/article/${comic.id}`,
            "isPartOf": {
              "@type": "WebSite",
              "name": "韩漫网",
              "url": "https://www.hanmanwang.com/"
            }
          }) }} />

          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "首页",
                "item": "https://www.hanmanwang.com/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": `${catName}漫画`,
                "item": `https://www.hanmanwang.com/category/${comic.category}`
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": comic.title
              }
            ]
          }) }} />

          <div className="px-4 md:px-6 py-6">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="flex-shrink-0 mx-auto md:mx-0">
                <div className="w-[220px] h-[310px] rounded-xl overflow-hidden bg-background-200">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} - 韩国漫画在线阅读`}
                    title={`${comic.title} - 韩国漫画在线阅读`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-3">
                  <strong>{comic.title}</strong>
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="text-sm text-foreground-500">作者：{comic.author}</span>
                  <span className="text-sm text-foreground-500">|</span>
                  <span className={`text-sm px-2 py-0.5 rounded-full whitespace-nowrap ${
                    comic.status === '连载中'
                      ? 'bg-accent-100 text-accent-700'
                      : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {comic.status}
                  </span>
                  <span className="text-sm text-foreground-500">|</span>
                  <span className="flex items-center text-sm text-amber-500">
                    <i className="ri-star-fill text-sm mr-0.5"></i>
                    {comic.rating} 分
                  </span>
                  <span className="text-sm text-foreground-500">|</span>
                  <span className="text-sm text-foreground-500">{comic.views} 阅读</span>
                </div>

                <div className="flex flex-wrap gap-2 mb-5">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs whitespace-nowrap"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  {comic.description}
                </p>

                <div className="flex flex-wrap gap-3">
                  <Link
                    to={`/article/${comic.id}`}
                    className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-full text-sm whitespace-nowrap transition-all duration-200"
                  >
                    开始阅读第一话
                  </Link>
                  <button className="px-6 py-2.5 bg-background-100 hover:bg-secondary-100 text-foreground-700 font-medium rounded-full text-sm whitespace-nowrap transition-all duration-200 border border-background-200">
                    <i className="ri-bookmark-line mr-1.5"></i>加入收藏
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-10">
              <h2 className="text-lg font-heading text-foreground-900 mb-4">
                <strong>章节列表</strong>
                <span className="text-sm font-normal text-foreground-500 ml-2">
                  共 {comic.chapters.length} 话
                </span>
              </h2>
              <div className="bg-background-50 rounded-xl border border-background-200/70 divide-y divide-background-200/60">
                {comic.chapters.map((chapter) => (
                  <Link
                    key={chapter.id}
                    to={`/article/${comic.id}`}
                    className="flex items-center justify-between px-5 py-3.5 hover:bg-background-100 transition-colors duration-200 group"
                  >
                    <span className="text-sm text-foreground-800 group-hover:text-primary-500 transition-colors whitespace-nowrap truncate">
                      {chapter.title}
                    </span>
                    <span className="text-xs text-foreground-400 flex-shrink-0 ml-4 whitespace-nowrap">
                      {chapter.date}
                    </span>
                  </Link>
                ))}
              </div>
            </div>

            <div className="mt-10">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-heading text-foreground-900 whitespace-nowrap">
                  <strong>同类推荐</strong>
                </h2>
                <Link
                  to={`/category/${comic.category}`}
                  className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
                >
                  更多{catName}漫画 <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>
              {relatedComics.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {relatedComics.map((rc) => (
                    <ComicCard key={rc.id} comic={rc} size="small" />
                  ))}
                </div>
              ) : (
                <p className="text-foreground-400 text-sm py-8 text-center">
                  暂无同类漫画推荐
                </p>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}